/**
 * 一单上的写操作：每一次用户动作一个幂等键（同一个动作重发沿用，换了动作才换），成功后把回包写进缓存——
 * 这一单、带回来的额度、这只宠物的单列表都刷新。不在这里自动重试：结果说不清时页面照实说，不偷偷再发一次。
 */
import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import type { TeachApi } from "./teach.service";
import type { TeachEntitlement, TeachOrder } from "./teachTypes";
import { teachKeys, useTeachApi } from "./useTeach";

type Result = TeachOrder | { order: TeachOrder; entitlement: TeachEntitlement };

export function useTeachAction<V>(order: TeachOrder, name: string, run: (api: TeachApi, key: string, value: V) => Promise<Result>, onDone?: (order: TeachOrder) => void) {
  const api = useTeachApi()!;
  const queryClient = useQueryClient();
  const userId = useOptionalCurrentHousehold()?.userId ?? "-";
  const key = useRef<{ value: string; key: string } | null>(null);
  return useMutation({
    mutationFn: (value: V) => {
      const sig = JSON.stringify(value ?? null);
      if (!key.current || key.current.value !== sig) key.current = { value: sig, key: newIdempotencyKey(`teach-${name}`) };
      return run(api, key.current.key, value);
    },
    onSuccess: (result) => {
      const next = "order" in result ? result.order : result;
      queryClient.setQueryData(teachKeys.order(userId, next.order_id), next);
      if ("entitlement" in result) queryClient.setQueryData(teachKeys.entitlement(userId), result.entitlement);
      void queryClient.invalidateQueries({ queryKey: teachKeys.orders(userId, order.pet_id) });
      void queryClient.invalidateQueries({ queryKey: teachKeys.entitlement(userId) });
      key.current = null;
      onDone?.(next);
    },
  });
}
